import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";

export type SeasonalPaymentRequestPayload = {
  lender_id: number;
  tier_id?: number;
  cadences: string[];
  first_payment_month?: number | null;
  notes?: string;
  term_months?: number;
  down_payment_amount?: number;
  monthly_payment?: number;
};

export type SeasonalPaymentRequestRecord = {
  id?: number;
  lender_id: number;
  tier_id?: number | null;
  cadences: string[];
  first_payment_month?: number | null;
  /** @deprecated Legacy field */
  defer_first_payment?: string | null;
  notes?: string;
};

export const CADENCE_OPTIONS = [
  { value: "quarterly", label: "Quarterly" },
  { value: "semi_annual", label: "Semi-annual" },
  { value: "annual", label: "Annual" },
] as const;

export const MONTH_OPTIONS = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
] as const;

export function cadenceLabel(value: string): string {
  const opt = CADENCE_OPTIONS.find((o) => o.value === value);
  return opt?.label ?? value;
}

export function monthLabel(month: number | null | undefined): string {
  if (month == null) return "";
  return MONTH_OPTIONS.find((m) => m.value === month)?.label ?? String(month);
}

/** One-line summary for application detail / lists */
export function formatSeasonalRequestSummary(req: SeasonalPaymentRequestRecord): string {
  const cadence = req.cadences?.[0];
  if (!cadence) return "—";
  const label = cadenceLabel(cadence);
  if (cadence === "annual") {
    const month =
      req.first_payment_month != null
        ? monthLabel(req.first_payment_month)
        : req.defer_first_payment
          ? `legacy: ${req.defer_first_payment}`
          : "";
    return month ? `${label} · First payment: ${month}` : label;
  }
  return label;
}

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  existing?: SeasonalPaymentRequestRecord | null;
  lenderId: number;
  tierId?: number;
  termMonths?: number;
  downPaymentAmount?: number;
  monthlyPayment?: number;
  isPending?: boolean;
  onSubmit: (payload: SeasonalPaymentRequestPayload) => void;
};

export function SeasonalPaymentRequestModal({
  open,
  onOpenChange,
  existing,
  lenderId,
  tierId,
  termMonths,
  downPaymentAmount,
  monthlyPayment,
  isPending,
  onSubmit,
}: Props) {
  const [cadence, setCadence] = useState<string>("");
  const [firstPaymentMonth, setFirstPaymentMonth] = useState<number | "">("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (!open) return;
    const existingCadence = existing?.cadences?.[0] ?? "";
    setCadence(existingCadence);
    setFirstPaymentMonth(
      existing?.first_payment_month != null ? existing.first_payment_month : ""
    );
    setNotes(existing?.notes || "");
  }, [open, existing]);

  const isAnnual = cadence === "annual";
  const canSubmit = Boolean(cadence) && (!isAnnual || (firstPaymentMonth !== "" && firstPaymentMonth >= 1));

  const handleSubmit = () => {
    if (!cadence) return;
    if (isAnnual && (firstPaymentMonth === "" || firstPaymentMonth < 1)) return;
    onSubmit({
      lender_id: lenderId,
      tier_id: tierId,
      cadences: [cadence],
      first_payment_month: isAnnual ? Number(firstPaymentMonth) : null,
      notes: notes.trim() || undefined,
      term_months: termMonths,
      down_payment_amount: downPaymentAmount,
      monthly_payment: monthlyPayment,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} maxWidth="max-w-md">
      <DialogContent className="p-6">
        <DialogHeader>
          <DialogTitle>
            {existing ? "Update seasonal payment request" : "Request seasonal payment schedule"}
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-gray-600 -mt-2">
          Monthly estimates on this offer are illustrative. We will ask the lender about non-monthly
          structures; final terms are subject to approval.
        </p>
        <div className="space-y-4 pt-2">
          <div>
            <Label className="text-sm font-medium">Payment cadence</Label>
            <div className="mt-2 space-y-2">
              {CADENCE_OPTIONS.map((opt) => (
                <label key={opt.value} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="radio"
                    name="payment_cadence"
                    checked={cadence === opt.value}
                    onChange={() => {
                      setCadence(opt.value);
                      if (opt.value !== "annual") {
                        setFirstPaymentMonth("");
                      }
                    }}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>
          {isAnnual && (
            <div>
              <Label htmlFor="first-payment-month" className="text-sm font-medium">
                When would you like your first payment?
              </Label>
              <select
                id="first-payment-month"
                value={firstPaymentMonth === "" ? "" : String(firstPaymentMonth)}
                onChange={(e) =>
                  setFirstPaymentMonth(e.target.value ? Number(e.target.value) : "")
                }
                className="mt-2 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="">Select month…</option>
                {MONTH_OPTIONS.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div>
            <Label htmlFor="seasonal-notes" className="text-sm font-medium">
              Notes (optional)
            </Label>
            <textarea
              id="seasonal-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Cash flow tied to harvest"
              className="mt-1 min-h-[72px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              maxLength={2000}
            />
          </div>
          <div className="flex gap-2 pt-2">
            <Button
              type="button"
              className="flex-1"
              disabled={isPending || !canSubmit}
              onClick={handleSubmit}
            >
              {isPending ? "Saving…" : existing ? "Update request" : "Submit request"}
            </Button>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function SeasonalPaymentRequestButton({
  onClick,
  className,
}: {
  /** @deprecated Only used for modal prefill; label does not change when a request exists */
  existing?: SeasonalPaymentRequestRecord | null;
  onClick: () => void;
  className?: string;
}) {
  return (
    <Button type="button" variant="outline" className={className} onClick={onClick}>
      Request seasonal payment schedule
    </Button>
  );
}
